import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Truck, Radio, ArrowRight, Plus } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import DriverLiveRouteDialog from '@/components/DriverLiveRouteDialog';
import API from '@/config/api';

const VansTodayWidget = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [summaries, setSummaries] = useState({}); // driver_id -> {total, onboard, delivered, absent}
  const [loading, setLoading] = useState(true);
  const [liveDriver, setLiveDriver] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/drivers`, getAuthHeaders());
        if (!mounted) return;
        const list = res.data || [];
        setDrivers(list);
        // Fetch live-route summary per driver in parallel
        const results = await Promise.all(
          list.map((d) =>
            axios
              .get(`${API}/drivers/${d.id}/live-route`, getAuthHeaders())
              .then((r) => [d.id, r.data])
              .catch(() => [d.id, null]),
          ),
        );
        if (!mounted) return;
        const map = {};
        for (const [id, data] of results) {
          if (!data) continue;
          const active = (data.children || []).filter((c) => !data.manifest?.[c.id]?.not_riding);
          map[id] = {
            total: active.length,
            onboard: active.filter((c) => data.manifest?.[c.id]?.status === 'onboard').length,
            delivered: active.filter((c) => data.manifest?.[c.id]?.status === 'delivered').length,
            absent: (data.children || []).length - active.length,
          };
        }
        setSummaries(map);
      } catch (err) {
        // Silent fail - widget hides itself
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
     
  }, []);

  if (loading) return null;

  return (
    <Card className="card-clay" data-testid="vans-today-widget">
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Truck className="w-5 h-5 text-primary" /> Vans Today
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/drivers')}
          data-testid="vans-today-manage-btn"
        >
          Manage <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </CardHeader>
      <CardContent>
        {drivers.length === 0 ? (
          <div className="text-center py-6 space-y-2" data-testid="vans-today-empty">
            <div className="text-sm text-muted-foreground">
              No drivers set up yet. Add a driver to start using the van manifest, live route map, and DCF trip logs.
            </div>
            <Button
              size="sm"
              className="btn-primary"
              onClick={() => navigate('/drivers')}
              data-testid="vans-today-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add a driver
            </Button>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {drivers.map((d) => {
              const s = summaries[d.id] || { total: 0, onboard: 0, delivered: 0, absent: 0 };
              return (
                <div
                  key={d.id}
                  className="rounded-lg border border-border p-3 flex items-center gap-3 hover:bg-muted/40 transition-colors"
                  data-testid={`vans-today-row-${d.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Truck className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{d.name}</div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <Badge variant="secondary" className="text-xs">
                        {s.total} riding
                      </Badge>
                      {s.onboard > 0 && (
                        <Badge className="text-xs bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-200">
                          {s.onboard} on van
                        </Badge>
                      )}
                      {s.delivered > 0 && (
                        <Badge className="text-xs bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200">
                          {s.delivered} delivered
                        </Badge>
                      )}
                      {s.absent > 0 && (
                        <Badge className="text-xs bg-slate-200 text-slate-700 hover:bg-slate-200">
                          {s.absent} absent
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setLiveDriver(d)}
                    className="flex-shrink-0"
                    data-testid={`vans-today-live-${d.id}`}
                  >
                    <Radio className="w-4 h-4 mr-1 text-emerald-600" /> Live
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <DriverLiveRouteDialog
        open={!!liveDriver}
        onClose={() => setLiveDriver(null)}
        driver={liveDriver}
      />
    </Card>
  );
};

export default VansTodayWidget;
